"use client";

import clsx from "clsx";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Tooltip from "@/components/library/Tooltip";
import Icon, { ICON_SIZES } from "@/components/library/Icon";
import { getCellText, writeClipboardMulti } from "./utils";
import { TableHoverContext } from "./TableHoverContext";

export const MarkdownTableTd: React.FC<
  React.HTMLAttributes<HTMLTableCellElement> & { children?: React.ReactNode }
> = (props) => {
  const cellRef = useRef<HTMLTableCellElement>(null);
  const [isFirstCell, setIsFirstCell] = useState(false);
  const [copied, setCopied] = useState(false);
  const { t: ct } = useTranslation("common");
  const hoverCtx = useContext(TableHoverContext);

  useEffect(() => {
    const td = cellRef.current;
    if (!td) return;
    setIsFirstCell(td.cellIndex === 0);
  }, []);

  const onCopyRow = () => {
    const td = cellRef.current;
    if (!td) return;
    const row = td.parentElement as HTMLTableRowElement | null;
    if (!row) return;
    const values = Array.from(row.cells).map((c) => getCellText(c));
    if (values.length === 0) return;
    const tsv = values.join("\t");
    const html = `<!DOCTYPE html><html><body><table><tr>${values
      .map((v) => `<td>${v}</td>`)
      .join("")}</tr></table></body></html>`;
    writeClipboardMulti(tsv, html).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    });
  };

  const { children, ...rest } = props;

  const rowIndex = cellRef.current?.parentElement
    ? (cellRef.current.parentElement as HTMLTableRowElement).rowIndex
    : -1;
  const showRowCopy =
    isFirstCell &&
    hoverCtx?.hoveredRowIndex !== null &&
    hoverCtx?.hoveredRowIndex === rowIndex;

  return (
    <td
      ref={cellRef}
      onMouseEnter={() => {
        const td = cellRef.current;
        if (!td) return;
        const row = td.parentElement as HTMLTableRowElement | null;
        hoverCtx?.setHoveredRowIndex(row ? row.rowIndex : null);
        hoverCtx?.setHoveredColIndex(td.cellIndex);
      }}
      {...rest}
    >
      <div className="relative">
        {isFirstCell && (
          <div
            className={clsx(
              "absolute -top-1 -right-1 z-10 transition-opacity duration-150",
              showRowCopy
                ? "opacity-100 pointer-events-auto"
                : "opacity-0 pointer-events-none"
            )}
            data-copy-control
          >
            {copied ? (
              <span className="text-[10px] text-500 bg-panel/90 backdrop-blur-sm rounded px-1.5 py-0.5">{ct("copied")}</span>
            ) : (
              <Tooltip content={ct("copyOptions.copyRow")} className="min-w-max">
                <button
                  type="button"
                  onClick={onCopyRow}
                  className="inline-flex items-center p-0.5 rounded text-300 hover:text-500 transition-colors"
                  aria-label={ct("copyOptions.copyRow")}
                  data-copy-control
                >
                  <Icon icon="copy" size={ICON_SIZES.XS} />
                </button>
              </Tooltip>
            )}
          </div>
        )}
        <div className="md-cell-body min-w-0 max-w-lg">{children}</div>
      </div>
    </td>
  );
};
